<template>
  <div :style="styleObject">
    <button
      style="display: none"
      type="button"
      class="btn-confirm-modal"
      data-bs-toggle="modal"
      data-bs-target="#modalConfirm"
    ></button>
    <div
      class="modal fade"
      :class="{ 'mlx-confirm': theme2 }"
      id="modalConfirm"
      tabindex="-1"
      aria-labelledby="modalConfirmLabel"
      aria-hidden="true"
    >
      <!-- KSK032: kiosk theme 2. MOO (isKiosk=false) keeps the markup below unchanged. -->
      <KskModalShell v-if="isKiosk" :width="680" :show-close="false">
        <div class="ksk-confirm" :class="`ksk-confirm--${ksk.tone}`">
          <div class="ksk-confirm__icon">
            <span class="material-icons-round">{{ ksk.icon }}</span>
          </div>
          <h2 v-if="ksk.title" id="modalConfirmLabel" class="ksk-confirm__title">{{ ksk.title }}</h2>
          <p class="ksk-confirm__message">{{ message }}</p>
        </div>
        <template #footer>
          <KskButton
            variant="outlined"
            size="md"
            class="btn-close-modal-confirm ksk-confirm__btn"
            data-bs-dismiss="modal"
          >{{ ksk.no }}</KskButton>
          <KskButton
            size="md"
            class="ksk-confirm__btn"
            :class="{ 'ksk-confirm__btn--danger': ksk.tone === 'danger' }"
            @click="processYes"
          >{{ ksk.yes }}</KskButton>
        </template>
      </KskModalShell>
      <div v-else class="modal-dialog">
        <div class="modal-content">
          <div class="modal-body text-center text-bold">{{ message }}</div>
          <div class="modal-footer d-flex justify-content-center">
            <button
              type="button"
              class="btn-close-modal-confirm"
              data-bs-dismiss="modal"
            >
              No
            </button>
            <button type="button" class="btn-yes" @click="processYes">
              Yes
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import KskModalShell from "@/views/kiosk/components/ui/KskModalShell.vue";
import KskButton from "@/views/kiosk/components/ui/KskButton.vue";

// KSK032 — P15 kiosk confirm copy. tone "danger" = amber icon + red confirm.
// KSK054: title/no/yes are i18n keys (kiosk.*), resolved in the `ksk` computed.
const KSK_CONFIRM_PRESETS = {
  startOver: { title: "kiosk.confirm.startOverTitle", no: "kiosk.confirm.keepOrdering", yes: "kiosk.confirm.yesStartOver", icon: "restart_alt", tone: "danger" },
  logout: { title: "kiosk.confirm.logoutTitle", no: "kiosk.confirm.keepOrdering", yes: "kiosk.confirm.yesLogOut", icon: "logout", tone: "danger" },
  cancelPayment: { title: "kiosk.confirm.cancelPaymentTitle", no: "kiosk.confirm.keepPaying", yes: "kiosk.confirm.yesCancel", icon: "cancel", tone: "danger" },
  switchDining: { title: "kiosk.confirm.switchDiningTitle", no: "kiosk.confirm.keepOrdering", yes: "kiosk.confirm.yesSwitch", icon: "swap_horiz", tone: "danger" },
  removeItem: { title: "kiosk.confirm.removeItemTitle", no: "kiosk.confirm.keepItem", yes: "kiosk.confirm.yesRemove", icon: "delete_outline", tone: "danger" },
};
const KSK_CONFIRM_DEFAULT = { title: "", no: "kiosk.common.no", yes: "kiosk.common.yes", icon: "help_outline", tone: "primary" };

export default {
  name: "ModalConfirm",
  components: {
    KskModalShell,
    KskButton,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    message: {
      type: String,
      default: "",
    },
    btnYes: {
      type: String,
      default: "",
    },
    processYes: {
      type: Function,
      default: () => {},
    },
    isKiosk: {
      type: Boolean,
      default: false,
    },
    // MTH097: opt-in theme2 skin, mirroring ModalNotification's `theme2` prop.
    // MyCart / OrderHistory2 / kiosk keep the original look by default.
    theme2: {
      type: Boolean,
      default: false,
    },
    // KSK032: kiosk-only copy, picked by preset (see KSK_CONFIRM_PRESETS).
    // No preset keeps the old Yes/No wording.
    kskPreset: {
      type: String,
      default: "",
    },
  },
  computed: {
    ksk() {
      const preset = KSK_CONFIRM_PRESETS[this.kskPreset] || KSK_CONFIRM_DEFAULT;
      return {
        ...preset,
        title: preset.title ? this.$t(preset.title) : "",
        no: this.$t(preset.no),
        yes: this.$t(preset.yes),
      };
    },
    styleObject() {
      // The Yes button follows the MAIN theme2 colour (same source as the
      // menu screen's `--color`), deliberately not primaryButtonColorTheme2.
      const accent =
        this.$store.state.mainThemeColorTheme2 || this.$store.state.color;
      return {
        "--color": this.$store.state.color,
        "--mlx-accent": accent,
        "--mlx-btn": accent,
      };
    },
  }
};
</script>

<style scoped>
.modal-dialog {
  top: 50%;
  transform: translate(0, -50%) !important;
}

.btn-close-modal-confirm,
.btn-yes {
  border: 1px solid orangered;
  background-color: orangered;
  border-radius: 5px;
  color: #ffffff;
  padding: 5px 30px;
  /* font-weight: bold; */
}

/* KSK032 — P15 kiosk confirm (theme 2), isKiosk only. Sized like the other
   kiosk dialogs (Still there?, Sign in, Dining): 32px title, 80px buttons. */
.ksk-confirm {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}
.ksk-confirm__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: var(--ksk-primary-container);
  color: var(--ksk-primary);
}
.ksk-confirm--danger .ksk-confirm__icon {
  background: #fdf0dc;
  color: #b25e00;
}
.ksk-confirm__icon .material-icons-round {
  font-size: 42px;
}
.ksk-confirm__title {
  margin: var(--ksk-space-2) 0 0;
  font-family: var(--ksk-font);
  font-size: var(--ksk-title-size);
  line-height: var(--ksk-title-lh);
  font-weight: 700;
  color: var(--ksk-text);
}
.ksk-confirm__message {
  margin: var(--ksk-space-1) 0 0;
  font-size: 20px;
  line-height: 28px;
  color: var(--ksk-text-secondary);
  white-space: pre-line;
}
/* No preset title: the message is the question itself, so it reads as the title. */
.ksk-confirm__icon + .ksk-confirm__message {
  margin-top: var(--ksk-space-2);
  font-size: var(--ksk-title-size);
  line-height: var(--ksk-title-lh);
  font-weight: 700;
  color: var(--ksk-text);
}
/* The shell's footer rule is scoped to the shell, so size the slotted buttons here. */
.ksk-confirm__btn {
  flex: 1 1 0;
  min-width: 0;
}
/* `btn-close-modal-confirm` must stay on the kiosk "No" button: the kiosk
   pages close this popup with $(".btn-close-modal-confirm").click() in
   closeAllModal(). Undo the legacy orangered rule above for the kiosk button. */
.ksk-btn.btn-close-modal-confirm {
  border: 2px solid var(--ksk-primary);
  border-radius: var(--ksk-radius-pill);
  background: var(--ksk-surface);
  color: var(--ksk-primary);
  padding: 0 var(--ksk-space-4);
}
.ksk-btn.ksk-confirm__btn--danger {
  background: var(--ksk-error);
  color: #ffffff;
}
.ksk-btn.ksk-confirm__btn--danger:active:not(:disabled) {
  background: #a51b19;
}

/* MTH097 — theme2 skin, opted in via the `theme2` prop (MOO Order.vue only).
   Everything above stays untouched for MyCart / OrderHistory2 / kiosk. */
.mlx-confirm {
  /* Bootstrap writes inline padding-left/right on .modal to compensate for the
     scrollbar, which pushes the centred card off-axis. Reset both. */
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.mlx-confirm .modal-dialog {
  max-width: 22rem;
  margin-left: auto;
  margin-right: auto;
  padding: 0 1rem;
  /* This branch removed the shared `.modal-dialog { top: 50% }` centring rule,
     so the theme2 skin centres itself the Bootstrap 5 way instead. */
  display: flex;
  align-items: center;
  min-height: calc(100% - 1rem);
}

.mlx-confirm .modal-content {
  width: 100%;
}

.mlx-confirm .modal-content {
  border: none;
  border-radius: 1.25rem;
  box-shadow: 0 20px 40px rgba(25, 28, 30, 0.18);
  overflow: hidden;
}

/* Mirrors ModalNotification's mlx-notify skin — same card, just two buttons
   sharing the footer row instead of one full-width button. */
.mlx-confirm .modal-body {
  padding: 1.75rem 1.25rem 1rem;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.45;
  color: #201a1a;
}

.mlx-confirm .modal-footer {
  border-top: none;
  justify-content: center;
  padding: 0 1.25rem 1.5rem;
  gap: 0.5rem;
  flex-wrap: nowrap;
}

.mlx-confirm .modal-footer > * {
  margin: 0;
}

.mlx-confirm .btn-close-modal-confirm,
.mlx-confirm .btn-yes {
  flex: 1;
  border-radius: 9999px;
  font-weight: 700;
  font-size: 0.95rem;
  padding: 0.55rem 1rem;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.mlx-confirm .btn-close-modal-confirm {
  border: 1px solid rgba(32, 26, 26, 0.25);
  background-color: transparent;
  color: #201a1a;
}

.mlx-confirm .btn-yes {
  border: 1px solid transparent;
  background-color: var(--mlx-btn, var(--color));
  color: #ffffff;
}

.mlx-confirm .btn-close-modal-confirm:active,
.mlx-confirm .btn-yes:active {
  transform: scale(0.97);
}
</style>
